import { ReportType } from "../../models/enumerations/report-type";
import FirestoreCondition from "../../models/interfaces/firestore-condition";
import FirestoreOrder from "../../models/interfaces/firestore-order";
import { Report } from "../../models/interfaces/report";
import { FbReportComplianceData, ReportComplianceData } from "../../models/interfaces/reports/report-compliance-data";
import { FbReportDetailData, ReportDetailData } from "../../models/interfaces/reports/report-detail-data";
import { FbReportMovementData, ReportMovementData } from "../../models/interfaces/reports/report-movement-data";
import { FbReportSummaryData, ReportSummaryData } from "../../models/interfaces/reports/report-summary-data";
import { FbReportWellnessSummaryData, ReportWellnessSummaryData } from "../../models/interfaces/reports/report-wellness-summary-data";
import { User } from "../../models/interfaces/user";
import FirestoreService from "./firestore-service";

const COLLECTION_NAME = "reportData";

// -----------------------------------------------------------------------------------------
// #region Service Methods
// -----------------------------------------------------------------------------------------

/**
 * Find the specific report by the id
 * @param {string} id - The Id of the report that we are retrieving
 * @returns {Promise<Report>} A promise for the Report we are retrieving
 */
const get = async (id: string) => {
    return FirestoreService.get<Report>(COLLECTION_NAME, id);
};

/**
 * Find the specific user by the id
 * @param {string} id - The Id of the user that we are retrieving
 * @returns {Promise<User>} A promise for the User we are retrieving
 */
const getBy = async (conditions: FirestoreCondition[], order: FirestoreOrder[] = [], limit?: number) => {
    return FirestoreService.getBy<Report>(COLLECTION_NAME, conditions, order, limit);
};

/**
 * Add a new detail report data to the reportsData collection
 * @param {Firestore} db
 * @param {Report} report - The report that is being added to the collection
 * @param {User} currentUser - The user that is logged into the application
 * @return {Promise<Report>} A promise to the newly added report
 */
const addDetailData = async (
    report: ReportDetailData,
    reportId: string,
    currentUser: User | null = null
) => {
    const fbReportData: FbReportDetailData = {
        ...report,
        reportId,
        type: ReportType.Detail,
    }
    return FirestoreService.add<FbReportDetailData>(COLLECTION_NAME, fbReportData, currentUser);
};

/**
 * Add a new detail report data to the reportsData collection
 * @param {Firestore} db
 * @param {Report} report - The report that is being added to the collection
 * @param {User} currentUser - The user that is logged into the application
 * @return {Promise<Report>} A promise to the newly added report
 */
const addMovementData = async (
    report: ReportMovementData,
    reportId: string,
    currentUser: User | null = null
) => {
    const fbReportData: FbReportMovementData = {
        ...report,
        reportId,
        type: ReportType.Movement,
    }
    return FirestoreService.add<FbReportMovementData>(COLLECTION_NAME, fbReportData, currentUser);
};

/**
 * Add a new compliance report data to the reportsData collection
 * @param {Firestore} db
 * @param {Report} report - The report that is being added to the collection
 * @param {User} currentUser - The user that is logged into the application
 * @return {Promise<Report>} A promise to the newly added report
 */
const addComplianceData = async (
    report: ReportComplianceData,
    reportId: string,
    currentUser: User | null = null
) => {
    const fbReportData: FbReportComplianceData = {
        ...report,
        reportId,
        type: ReportType.Compliance,
    }
    return FirestoreService.add<FbReportComplianceData>(COLLECTION_NAME, fbReportData, currentUser);
};

const addWellnessSummaryData = async (
    report: ReportWellnessSummaryData,
    reportId: string,
    currentUser: User | null = null
) => {
    const fbReportData: FbReportWellnessSummaryData = {
        ...report,
        reportId,
        type: ReportType.Wellness,
    }
    return FirestoreService.add<FbReportWellnessSummaryData>(COLLECTION_NAME, fbReportData, currentUser);
};

/**
 * Add a new summary report data to the reportsData collection
 * @param {Firestore} db
 * @param {Report} report - The report that is being added to the collection
 * @param {User} currentUser - The user that is logged into the application
 * @return {Promise<Report>} A promise to the newly added report
 */
const addSummaryData = async (
    report: ReportSummaryData,
    reportId: string,
    currentUser: User | null = null
) => {
    const fbReportData: FbReportSummaryData = {
        ...report,
        reportId,
        type: ReportType.Summary,
    }
    return FirestoreService.add<FbReportSummaryData>(COLLECTION_NAME, fbReportData, currentUser);
};

// #endregion Service Methods


// -----------------------------------------------------------------------------------------
// #region Exports
// -----------------------------------------------------------------------------------------

const ReportDataService = {
    get,
    getBy,
    addDetailData,
    addMovementData,
    addComplianceData,
    addWellnessSummaryData,
    addSummaryData,
};

export default ReportDataService;

// #endregion Exports